import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
  QueryList,
  signal,
  untracked,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { FilteredCreatorBadgeComponent } from '@shared/components/filtered-creator-badge/filtered-creator-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SocialContentTileComponent } from '@shared/components/social-content-tile/social-content-tile.component';
import {
  ITileConfiguration,
  TTileContentStatus,
} from '@shared/components/social-content-tile/social-content-tile.interface';
import { SocialContentTileService } from '@shared/components/social-content-tile/social-content-tile.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, map, startWith, Subscription } from 'rxjs';
import {
  EDetailLevel,
  ICampaignExportApi,
} from '../campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import {
  IDeepLinkContentsFilter,
  IDeepLinkCreatorInfo,
} from '../campaign-details-deep-link/campaign-details-deep-link.interface';
import { CampaignDetailsDeliverablesFilterSelectComponent } from '../campaign-details-deliverables/campaign-details-deliverables-filter-select/campaign-details-deliverables-filter-select.component';
import { CampaignDetailsDeliverablesStatusFiltersComponent } from '../campaign-details-deliverables/campaign-details-deliverables-status-filters/campaign-details-deliverables-status-filters.component';
import {
  ICampaignDetailsDeliverablesFilterOption,
  ICampaignDetailsDeliverablesStatusOption,
} from '../campaign-details-deliverables/campaign-details-deliverables.interface';
import { ICampaignExportConfig } from '../campaign-export-config/campaign-export-config.interface';

@Component({
  animations: [
    trigger('expandCollapse', [
      state('expanded', style({ maxHeight: '56px', opacity: '1' })),
      state('collapsed', style({ maxHeight: '0px', opacity: '0' })),
      transition('collapsed <=> expanded', animate('200ms ease-in-out')),
    ]),
  ],
  imports: [
    SocialContentTileComponent,
    NgxSkeletonLoaderModule,
    DotsSpinnerComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    TranslateModule,
    ReactiveFormsModule,
    IconComponent,
    CampaignDetailsDeliverablesFilterSelectComponent,
    CampaignDetailsDeliverablesStatusFiltersComponent,
    FilteredCreatorBadgeComponent,
  ],
  selector: 'app-social-content-tiles-list',
  styleUrl: './social-content-tiles-list.component.scss',
  templateUrl: './social-content-tiles-list.component.html',
})
export class SocialContentTilesListComponent implements AfterViewInit, OnDestroy {
  public readonly analyticsNodes = input.required<IAnalyticsNode[] | null>();
  public readonly filteredCreator = input<IDeepLinkCreatorInfo | null>(null);

  public readonly campaignExportConfig = input<ICampaignExportConfig | null>(null);
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly briefingTimeline = input<boolean>(false);
  public readonly loading = input<boolean>(true);
  public readonly exportMode = input<boolean>(false);
  public readonly campaignExport = input<ICampaignExportApi | null>(null);
  public readonly noCampaignContextMode = input<boolean>(false);
  public readonly resetToPlanned = input<number>(0);
  public readonly deepLinkFilters = input<IDeepLinkContentsFilter | null>(null);
  // Identifies which filter/date-range the currently-loaded analyticsNodes() belongs to (see
  // ICampaignDetailsQueryState.filterSignature in campaign-details.store.ts - it's updated to
  // the NEW signature the instant a re-fetch starts, before the new data arrives). Only
  // campaign-details.component.html passes this; other callers leave it at the default null,
  // matched by desiredFilterSignature()'s own default below, so awaitingFilterRefresh() (see
  // below) degrades to null === null - inert - for them.
  public readonly filterSignature = input<string | null>(null);
  // No longer read (see awaitingFilterRefresh() below for why filterSignature()/
  // desiredFilterSignature() equality alone is now sufficient) - kept as an input since
  // campaign-details.component.html still passes it and other consumers may want it.
  public readonly hasQueryError = input<boolean>(false);
  // The filter/date-range signature the user CURRENTLY wants applied (see
  // CampaignDetailsStore.currentFilterSignature$) - distinct from filterSignature() above, which
  // is the signature the currently-loaded analyticsNodes() actually belongs to and which reverts
  // to the last successful one on a failed fetch. The two can end up equal even while the user has
  // since moved on to (or back to) something else - see awaitingFilterRefresh() below for why that
  // distinction matters. Only campaign-details.component.html passes this; other callers leave it
  // at the default null, matching filterSignature()'s own default, so they're unaffected.
  public readonly desiredFilterSignature = input<string | null>(null);

  public readonly tileClicked = output<ITileConfiguration>();
  public readonly filteredCreatorRemoved = output<void>();

  public readonly visibleCount = signal<number>(18);
  public readonly searchQuery = signal<string>('');
  public readonly selectedMatchingTypes = signal<Set<EMatchingType>>(new Set());
  public readonly selectedContentStatuses = signal<Set<TTileContentStatus>>(new Set());
  public readonly selectedBriefingStatuses = signal<Set<EMatchingType>>(new Set([EMatchingType.planned]));
  public readonly onlyWithObservationFindings = signal<boolean>(false);

  public readonly searchControl = new FormControl<string>('');

  private readonly translate = inject(TranslateService);

  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  private readonly effectiveAnalyticsNodes = computed(() => {
    const nodes = this.analyticsNodes();
    const creator = this.filteredCreator();

    if (!creator || !nodes) {
      return nodes;
    }

    return nodes.filter((node) => node.creatorId === creator.id);
  });

  public readonly tiles = computed(
    (): ITileConfiguration[] =>
      this.effectiveAnalyticsNodes()?.map((analyticsNode) =>
        this.socialContentTileService.prepareTileData(analyticsNode),
      ) ?? [],
  );

  // loading() also flips true for background refreshes of already-loaded data (periodic
  // re-fetch, tab switch, filter change), not just the first load - gating the skeleton on
  // that alone tore down and rebuilt every tile (and every already-loaded thumbnail image
  // inside it) on every refresh. analyticsNodes() only reverts to null when there's genuinely
  // no data yet (a fresh campaign navigation resets the underlying store state) - it's never
  // cleared just because a background re-fetch started, so gating on it here specifically
  // means visibleTiles() below keeps rendering the same tiles (matched by track tile.id) with
  // the same images through a background refresh, and only the true first load shows a
  // skeleton for that clause. awaitingFilterRefresh() covers the other case where
  // analyticsNodes() stays non-null but now belongs to a stale filter/date-range (see that
  // computed below) - it's deliberately OR'd in on its own, NOT gated behind loading() like the
  // first clause: loading() (bound to `!data || inFlight`, see campaign-details.component.html)
  // turns false the instant a fetch attempt ends, whether it succeeded or failed, because `data`
  // is never null once a filter's first fetch has ever succeeded. If this were `loading() &&
  // awaitingFilterRefresh()`, a FAILED filter-change fetch would have loading() go false while
  // awaitingFilterRefresh() was still (correctly) true - see that computed's own comment below -
  // and the && would make the whole expression false, silently rendering the PREVIOUS filter's
  // tiles as if they belonged to the new one.
  // Keeping awaitingFilterRefresh() unconditional means the skeleton keeps showing through that
  // failure instead, until a fetch for the new filter actually comes back clean.
  public readonly showInitialSkeleton = computed(
    (): boolean => (this.loading() && this.analyticsNodes() === null) || this.awaitingFilterRefresh(),
  );

  // True whenever the currently-loaded analyticsNodes() (filterSignature()) don't belong to what's
  // CURRENTLY desired (desiredFilterSignature()) - i.e. the tiles on screen are stale for a
  // filter/date-range change that's already been requested. This used to be a manually
  // armed/cleared signal (set true by an effect watching filterSignature(), cleared by a second
  // effect watching loading()/hasQueryError()/isShowingDesiredFilter()) - that pairing could
  // desync: if the whole fetch (start AND finish) resolved within the same Angular change-detection
  // flush - always true for the demo's synchronous/microtask-backed fake services, and possible
  // for a very fast real response too - both effects become dirty in the SAME flush, and whichever
  // one happened to run first could observe the OTHER's pre-update value, permanently arming this
  // flag with no later signal change left to trigger a re-check (a plain computed() has no such
  // ordering hazard - every read re-derives the current answer from the current inputs, so there's
  // no "armed but never re-checked" state to get stuck in).
  //
  // Reduces to exactly the old two-effect behavior for every case that pair was written to handle:
  // - Filter unchanged (including the initial null -> value load, already covered by the other
  //   clause of showInitialSkeleton()): signatures match, false.
  // - Filter changed, fetch in flight or succeeded for the NEW filter: filterSignature() catches
  //   up to desiredFilterSignature() the moment it resolves, so this flips back to false as soon as
  //   (and only once) the new data is actually in - no separate "loading" tracking needed here,
  //   analyticsNodes()'s own filterSignature() input already carries that.
  // - Filter changed, fetch FAILED: filterSignature() reverts to the last-confirmed one (see
  //   CampaignDetailsStore.handleTabRequestError/handleTabProcessingError), which still disagrees
  //   with the new desiredFilterSignature() - stays true, same as before.
  // - User then reselects the filter that's still actually on screen: desiredFilterSignature()
  //   moves back to match the (unchanged) filterSignature() - false again, regardless of
  //   hasQueryError() - this is the exact case isShowingDesiredFilter() was introduced for, and
  //   it's handled here for free since it's just signature equality either way.
  private readonly awaitingFilterRefresh = computed(
    (): boolean => this.filterSignature() !== this.desiredFilterSignature(),
  );

  public readonly matchingTypeOptions = computed((): ICampaignDetailsDeliverablesFilterOption<string>[] => {
    this.languageChange();
    const tiles = this.tiles();
    const distinctTypes = new Set(tiles.map((tile) => tile.matchingType));

    return [...distinctTypes].map((type) => ({
      label: this.translate.instant(`shared.statuses.${type}`) as string,
      value: type,
    }));
  });

  public readonly matchingEmptyLabel = computed((): string => {
    this.languageChange();

    return this.translate.instant('pages.campaignDetails.contents.filters.allMatchings', {
      number: this.matchingTypeOptions().length,
    }) as string;
  });

  public readonly matchingAndSearchFilteredTiles = computed((): ITileConfiguration[] => {
    const query = this.searchQuery().toLowerCase();
    const tiles = this.tiles();
    const selectedMatchingTypes = this.selectedMatchingTypes();

    return tiles.filter(
      (tile) =>
        this.isTileIncludesSearchQuery(tile, query) &&
        (selectedMatchingTypes.size === 0 || selectedMatchingTypes.has(tile.matchingType)),
    );
  });

  public readonly matchedAndSearchFilteredTiles = computed((): ITileConfiguration[] => {
    const query = this.searchQuery().toLowerCase();
    const tiles = this.tiles();

    return tiles.filter(
      (tile) => tile.matchingType === EMatchingType.matched && this.isTileIncludesSearchQuery(tile, query),
    );
  });

  public readonly contentStatusOptions = computed((): ICampaignDetailsDeliverablesStatusOption[] => {
    const tiles = this.matchedAndSearchFilteredTiles();
    const selectedContentStatuses = this.selectedContentStatuses();
    const allStatuses: TTileContentStatus[] = ['notFulfilled', 'partiallyFulfilled', 'fulfilled'];

    return allStatuses.map((status, index) => {
      const count = tiles.filter((tile) => tile.contentStatus === status).length;

      return {
        colorScheme: 'content-status',
        count,
        isDisabled: count === 0 && !selectedContentStatuses.has(status),
        key: status,
        sortOrder: index,
        status,
      };
    });
  });

  public readonly showContentStatusFilter = computed((): boolean => {
    if (this.campaignExportConfig()?.showContentsStatusFilter === false) {
      return false;
    }

    const selectedMatchingTypes = this.selectedMatchingTypes();

    if (selectedMatchingTypes.size > 0 && !selectedMatchingTypes.has(EMatchingType.matched)) {
      return false;
    }

    return this.tiles().some((tile) => tile.matchingType === EMatchingType.matched);
  });

  /** Maps API EAnalyticsStatus values (from allowedContentStatuses) to TTileContentStatus display keys. */
  private static readonly CONTENT_API_TO_DISPLAY: Partial<Record<EAnalyticsStatus, TTileContentStatus>> = {
    [EAnalyticsStatus.failed]: 'notFulfilled',
    [EAnalyticsStatus.problem]: 'partiallyFulfilled',
    [EAnalyticsStatus.ok]: 'fulfilled',
  };

  public readonly visibleContentStatusOptions = computed((): ICampaignDetailsDeliverablesStatusOption[] => {
    const allowed = this.campaignExportConfig()?.allowedContentStatuses ?? null;
    const options = this.contentStatusOptions();

    if (allowed === null) {
      return options;
    }

    const allowedDisplay = new Set(
      allowed
        .map((s) => SocialContentTilesListComponent.CONTENT_API_TO_DISPLAY[s])
        .filter((s): s is TTileContentStatus => s !== undefined),
    );

    return options.filter((o) => allowedDisplay.has(o.key as TTileContentStatus));
  });

  public readonly briefingStatusOptions = computed((): ICampaignDetailsDeliverablesStatusOption[] => {
    const tiles = this.tiles();
    const plannedCount = tiles.filter((t) => t.matchingType === EMatchingType.planned).length;
    const missingCount = tiles.filter((t) => t.matchingType === EMatchingType.missing).length;
    const otherCount = tiles.filter(
      (t) => t.matchingType === EMatchingType.matched || t.matchingType === EMatchingType.bonus,
    ).length;
    const selected = this.selectedBriefingStatuses();

    return [
      {
        colorScheme: 'matching-status-borderless',
        count: missingCount,
        isDisabled: missingCount === 0 && !selected.has(EMatchingType.missing),
        key: EMatchingType.missing,
        sortOrder: 0,
        status: EMatchingType.missing,
      },
      {
        colorScheme: 'matching-status-borderless',
        count: plannedCount,
        isDisabled: plannedCount === 0 && !selected.has(EMatchingType.planned),
        key: EMatchingType.planned,
        sortOrder: 1,
        status: EMatchingType.planned,
      },
      {
        colorScheme: 'matching-status-borderless',
        count: otherCount,
        isDisabled: otherCount === 0 && !selected.has(EMatchingType.other),
        key: EMatchingType.other,
        sortOrder: 2,
        status: EMatchingType.other,
      },
    ];
  });

  public readonly visibleTiles = computed((): ITileConfiguration[] => {
    if (this.briefingTimeline()) {
      return this.filteredTiles();
    }

    const all = this.filteredTiles();

    return all.slice(0, this.visibleCount());
  });

  public readonly filteredTiles = computed((): ITileConfiguration[] => {
    if (this.briefingTimeline()) {
      const tiles = this.tiles();
      const selected = this.selectedBriefingStatuses();

      if (selected.size === 0) {
        return tiles;
      }

      return tiles.filter((tile) => {
        if (
          selected.has(EMatchingType.other) &&
          (tile.matchingType === EMatchingType.matched || tile.matchingType === EMatchingType.bonus)
        ) {
          return true;
        }

        return selected.has(tile.matchingType);
      });
    }

    const selectedContentStatuses = this.selectedContentStatuses();
    const onlyWithObservationFindings = this.onlyWithObservationFindings();

    let result: ITileConfiguration[];

    if (selectedContentStatuses.size > 0) {
      result = this.matchedAndSearchFilteredTiles().filter((tile) => selectedContentStatuses.has(tile.contentStatus!));
    } else {
      result = this.matchingAndSearchFilteredTiles();
    }

    if (onlyWithObservationFindings) {
      return result.filter((tile) => tile.observationCount > 0);
    }

    return result;
  });

  public readonly hasMore = computed((): boolean => this.visibleCount() < this.filteredTiles().length);

  public readonly isAutoLoading = signal<boolean>(false);

  public randomTileHeights: number[] = [];
  public EDetailLevel = EDetailLevel;

  @ViewChild('sentinel', { static: false })
  public set sentinel(sentinelRef: ElementRef<HTMLDivElement> | undefined) {
    this.sentinelElement = sentinelRef?.nativeElement;
    this.setupIntersectionObserver();
  }
  @ViewChild('masonryContainer', { static: false })
  private readonly masonryContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren('masonryItem')
  private readonly masonryItems?: QueryList<ElementRef<HTMLDivElement>>;

  private readonly socialContentTileService = inject(SocialContentTileService);
  private readonly ngZone = inject(NgZone);

  private readonly tileWidth: number = 248;
  private readonly tileGap: number = 28;

  private readonly subscriptions: Subscription[] = [];

  private sentinelElement?: HTMLDivElement;
  private intersectionObserver?: IntersectionObserver;
  private defaultMatchingApplied = false;
  private isLoadingMore = false;
  private resizeObserver?: ResizeObserver;

  constructor() {
    this.createDefaultMatchingTypesEffect();
    this.createResetVisibleTilesEffect();
    this.createResetVisibleCountOnFilterChangeEffect();
    this.generateRandomTileHeights();
    this.createFiltersSubscriptionEffect();
    this.createResetToPlannedEffect();
    this.createResetContentStatusOnMatchingTypeChangeEffect();
    this.createDeepLinkFiltersEffect();
    this.createRelayoutOnDataChangeEffect();
  }

  public ngAfterViewInit(): void {
    this.setupIntersectionObserver();

    if (this.masonryItems) {
      this.masonryItems.changes.subscribe((): void => {
        this.updateMasonryLayout();
      });
    }

    if (this.masonryContainer) {
      this.resizeObserver = new ResizeObserver((): void => {
        this.updateMasonryLayout();
      });
      this.resizeObserver.observe(this.masonryContainer.nativeElement);
    }

    this.updateMasonryLayout();
  }

  public generateRandomTileHeights(): void {
    effect(() => {
      this.randomTileHeights = Array.from({ length: 10 }, () => this.generateRandomTileHeight());
    });
  }

  public generateRandomTileHeight(): number {
    return this.briefingTimeline() ? 280 : Math.floor(Math.random() * 321) + 180;
  }

  public ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.subscriptions?.forEach((subscription) => subscription?.unsubscribe());
  }

  private createDefaultMatchingTypesEffect(): void {
    effect(() => {
      if (this.defaultMatchingApplied) {
        return;
      }

      const options = this.matchingTypeOptions();

      if (!options.length) {
        return;
      }

      const hasUnrelated = options.some((o) => (o.value as EMatchingType) === EMatchingType.unrelated);

      if (!hasUnrelated) {
        return;
      }

      // Deep-link or pre-set selections always win over defaults
      const hasDeepLinkMatchingTypes = this.deepLinkFilters()?.matchingTypes !== undefined;
      const hasExistingSelection = this.selectedMatchingTypes().size > 0;

      this.defaultMatchingApplied = true;

      if (hasDeepLinkMatchingTypes || hasExistingSelection) {
        return;
      }

      const defaults = new Set(
        options.map((o) => o.value as EMatchingType).filter((v) => v !== EMatchingType.unrelated),
      );

      untracked(() => {
        this.selectedMatchingTypes.set(defaults);
      });
    });
  }

  private createFiltersSubscriptionEffect(): void {
    effect(() => {
      this.subscriptions.push(
        this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
          this.searchQuery.set(value ?? '');
        }),
      );
    });
  }

  private createResetToPlannedEffect(): void {
    effect(() => {
      const counter = this.resetToPlanned();

      if (counter > 0) {
        untracked(() => {
          this.selectedBriefingStatuses.set(new Set([EMatchingType.planned]));
        });
      }
    });
  }

  private createResetContentStatusOnMatchingTypeChangeEffect(): void {
    effect(() => {
      const selectedMatchingTypes = this.selectedMatchingTypes();

      if (selectedMatchingTypes.size > 0 && !selectedMatchingTypes.has(EMatchingType.matched)) {
        untracked(() => {
          if (this.selectedContentStatuses().size > 0) {
            this.selectedContentStatuses.set(new Set());
          }
        });
      }
    });
  }

  public onMatchingTypesChanged(values: Set<string>): void {
    this.selectedMatchingTypes.set(values as Set<EMatchingType>);
  }

  public onContentStatusesChanged(statuses: Set<unknown>): void {
    this.selectedContentStatuses.set(statuses as Set<TTileContentStatus>);
  }

  public onBriefingStatusesChanged(statuses: Set<unknown>): void {
    this.selectedBriefingStatuses.set(statuses as Set<EMatchingType>);
  }

  private isTileIncludesSearchQuery(tile: ITileConfiguration, query: string): boolean {
    if (!query) {
      return true;
    }

    return !!(
      tile.title?.toLowerCase().includes(query) ||
      tile.description?.toLowerCase().includes(query) ||
      tile.nonExecutedCtaName?.toLowerCase().includes(query) ||
      tile.creator.name.toLowerCase().includes(query) ||
      tile.platformFeature.toLowerCase().includes(query) ||
      tile.executionStatus.toLowerCase().includes(query) ||
      tile.executionDateTime?.toLowerCase().includes(query)
    );
  }

  private loadMore(): void {
    const total = this.filteredTiles().length;
    const current = this.visibleCount();

    if (current >= total) {
      this.isAutoLoading.set(false);
      this.isLoadingMore = false;

      return;
    }

    const increment = 9;
    this.visibleCount.set(Math.min(current + increment, total));

    this.updateMasonryLayout();

    // After layout update, check if sentinel is still visible and load more if needed
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (this.sentinelElement && this.visibleCount() < total) {
          const rect = this.sentinelElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const rootMarginBottom = 400;
          const isSentinelVisible = rect.top <= viewportHeight + rootMarginBottom;

          if (isSentinelVisible) {
            this.isAutoLoading.set(true);
            this.ngZone.run(() => {
              this.loadMore();
            });
          } else {
            this.isAutoLoading.set(false);
            this.isLoadingMore = false;
          }
        } else {
          this.isAutoLoading.set(false);
          this.isLoadingMore = false;
        }
      }, 150);
    });
  }

  private setupIntersectionObserver(): void {
    if (!this.sentinelElement) {
      return;
    }

    this.intersectionObserver?.disconnect();

    this.intersectionObserver = new IntersectionObserver(
      (entries): void => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.isLoadingMore) {
            this.isLoadingMore = true;
            this.ngZone.run(() => {
              this.loadMore();
            });
          }
        }
      },
      { root: null, rootMargin: '0px 0px 400px 0px', threshold: 0 },
    );

    this.intersectionObserver.observe(this.sentinelElement);
  }

  private updateMasonryLayout(): void {
    if (!this.masonryContainer || !this.masonryItems || this.masonryItems.length === 0) {
      return;
    }

    this.ngZone.runOutsideAngular((): void => {
      window.requestAnimationFrame((): void => {
        setTimeout((): void => {
          this.applyMasonryLayout();
        }, 100);
      });
    });
  }

  private applyMasonryLayout(): void {
    const container = this.masonryContainer!.nativeElement;
    const containerWidth: number = container.offsetWidth;

    if (!containerWidth) {
      return;
    }

    const columnsPerRow: number = Math.floor((containerWidth + this.tileGap) / (this.tileWidth + this.tileGap));

    if (columnsPerRow === 0) {
      return;
    }

    const totalTileWidth: number = columnsPerRow * this.tileWidth;
    const remainingSpace: number = containerWidth - totalTileWidth;
    const calculatedGap: number = columnsPerRow > 1 ? remainingSpace / (columnsPerRow - 1) : 0;
    const items = this.masonryItems!.toArray();

    if (this.briefingTimeline()) {
      this.applyEqualRowHeightLayout(container, items, columnsPerRow, calculatedGap);
    } else {
      this.applyFreeColumnLayout(container, items, columnsPerRow, calculatedGap);
    }
  }

  private applyEqualRowHeightLayout(
    container: HTMLDivElement,
    items: ElementRef<HTMLDivElement>[],
    columnsPerRow: number,
    calculatedGap: number,
  ): void {
    for (const item of items) {
      item.nativeElement.style.height = 'auto';
      item.nativeElement.style.opacity = '0';
    }

    let currentTop = 0;
    const totalRows = Math.ceil(items.length / columnsPerRow);

    for (let row = 0; row < totalRows; row++) {
      const rowStart = row * columnsPerRow;
      const rowEnd = Math.min(rowStart + columnsPerRow, items.length);
      let maxHeight = 0;

      for (let i = rowStart; i < rowEnd; i++) {
        const h = items[i].nativeElement.offsetHeight;

        if (h > maxHeight) maxHeight = h;
      }

      for (let i = rowStart; i < rowEnd; i++) {
        const col = i - rowStart;
        const el = items[i].nativeElement;

        el.style.left = `${col * (this.tileWidth + calculatedGap)}px`;
        el.style.top = `${currentTop}px`;
        el.style.height = `${maxHeight}px`;
        el.style.opacity = '1';
      }

      currentTop += maxHeight + this.tileGap;
    }

    container.style.height = `${Math.max(currentTop - this.tileGap + 20, 0)}px`;
  }

  private applyFreeColumnLayout(
    container: HTMLDivElement,
    items: ElementRef<HTMLDivElement>[],
    columnsPerRow: number,
    calculatedGap: number,
  ): void {
    const columnHeights: number[] = new Array(columnsPerRow).fill(0);

    for (let i = 0; i < items.length; i++) {
      const item = items[i].nativeElement;
      const height: number = item.offsetHeight;

      if (!height) {
        continue;
      }

      const columnIndex: number = i % columnsPerRow;
      const left: number = columnIndex * (this.tileWidth + calculatedGap);
      const top: number = columnHeights[columnIndex];

      item.style.left = `${left}px`;
      item.style.top = `${top}px`;
      item.style.opacity = '1';

      columnHeights[columnIndex] += height + this.tileGap;
    }

    container.style.height = `${Math.max(...columnHeights, 0)}px`;
  }

  private createResetVisibleTilesEffect(): void {
    effect(() => {
      const totalTiles = this.tiles().length;

      // When data refreshes, keep current visibleCount but ensure the intersection
      // observer is re-setup and load more tiles if the sentinel is already visible.
      if (totalTiles > 0 && this.sentinelElement) {
        this.ngZone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.loadMoreUntilSentinelNotVisible();
              this.setupIntersectionObserver();
            }, 200);
          });
        });
      } else if (this.sentinelElement) {
        this.ngZone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            this.setupIntersectionObserver();
          });
        });
      }
    });
  }

  private createResetVisibleCountOnFilterChangeEffect(): void {
    effect(() => {
      // Tracking only filter signals — not data signals — so that periodic data
      // refreshes do not reset the user's scroll/pagination position.
      this.searchQuery();
      this.selectedMatchingTypes();
      this.selectedContentStatuses();
      this.selectedBriefingStatuses();
      this.visibleCount.set(18);
    });
  }

  private loadMoreUntilSentinelNotVisible(): void {
    const total = this.filteredTiles().length;
    const current = this.visibleCount();

    if (current >= total || !this.sentinelElement) {
      this.isAutoLoading.set(false);
      this.isLoadingMore = false;

      return;
    }

    if (this.isLoadingMore) {
      return;
    }

    this.isLoadingMore = true;
    const rect = this.sentinelElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const rootMarginBottom = 400;
    const isSentinelVisible = rect.top <= viewportHeight + rootMarginBottom;

    if (isSentinelVisible && current < total) {
      this.isAutoLoading.set(true);
      const increment = 9;
      this.visibleCount.set(Math.min(current + increment, total));

      this.updateMasonryLayout();

      // Recursively check and load more after layout settles
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.loadMoreUntilSentinelNotVisible();
        }, 150);
      });
    } else {
      this.isAutoLoading.set(false);
      this.isLoadingMore = false;
    }
  }

  // Before this component stopped tearing down and rebuilding every tile on a background
  // refresh, masonryItems.changes (fired only when the DOM's set of tile elements actually
  // changes) was enough to keep the masonry layout correct, because a refresh always changed
  // that set. Now that unchanged tiles persist across a refresh, a tile whose rendered height
  // changes (e.g. updated KPI text) without the tile SET itself changing wouldn't otherwise
  // trigger a relayout - watch the actual rendered data directly so this can't go stale.
  private createRelayoutOnDataChangeEffect(): void {
    effect(() => {
      this.visibleTiles();

      untracked(() => {
        this.updateMasonryLayout();
      });
    });
  }

  private createDeepLinkFiltersEffect(): void {
    effect(() => {
      const filters = this.deepLinkFilters();

      if (!filters) {
        return;
      }

      untracked(() => {
        this.selectedMatchingTypes.set(filters.matchingTypes ?? new Set());
        this.selectedContentStatuses.set(filters.contentStatuses ?? new Set());
      });
    });
  }
}
